import {
    BreachEvent,
    BreachScreenContext,
    click_origin_breach_screen,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import React, { useContext, useRef } from 'react'
import { IconStar } from '../../../../icons/IconStar/IconStar'
import { useAuthActions } from '../../../../user-registration/authentication/AuthenticationProvider'
import { useSection, useSectionMetaInfo } from '../../../../__helpers'
import { TheWestSection } from '../../../../__styling'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { ComputedPackageImage } from './ComputedPackageImage'
import {
    Ribbon,
    StyledPackageImage,
    StyledSubscribeWithGoogleButton,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackage,
    StyledSubscriptionPackageActions,
    StyledSubscriptionPackageButton,
    StyledSubscriptionPackageHeading,
    StyledSubscriptionPackageOriginalPrice,
    StyledSubscriptionPackagePrice,
    StyledSubscriptionPackagePriceQualifier,
} from './SubscriptionPackage.styled'

const everyDayDigitalImage = require('./images/everyday_digital.png')
const everyDayNewspaperImage = require('./images/everyday_digital_newspaper.png')

export interface SubscriptionPackageProps {
    pretitle: string
    originalPrice?: string
    heading: string
    price: string
    priceQualifier?: string
    ribbonTitle?: string
    packagePath: string
    offerCode?: string
    packageType?: 'digital' | 'digitalprint'
    breachCreativeName: string
    breachDeal: string
    packageTheme?: 'default' | 'red'
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
}

export const SubscriptionPackage: React.FC<SubscriptionPackageProps> = ({
    pretitle,
    heading,
    originalPrice,
    price,
    priceQualifier,
    ribbonTitle,
    packagePath,
    breachDeal,
    offerCode,
    breachCreativeName,
    packageTheme = 'default',
    onEvent,
    packageType,
    cancellationMessage,
    googleSKU,
}) => {
    const { onSubscribeClick } = useAuthActions()
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const section = useSection<TheWestSection>()

    // Matches swg-breach-albany-advertiser etc
    const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
        `swg-breach-${section}`,
    )

    const buttonRef = useRef<HTMLButtonElement>(null)

    const showSwG =
        subscribeWithGoogleEnabled &&
        subscribeWithGoogleBreachInterfaceEnabled &&
        googleSKU

    const sectionMeta = useSectionMetaInfo()

    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context

    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        googleSKU || 'everyday_digital_4weeks',
        sectionMeta,
        breachContext,
    )

    return (
        // Note: There is high editorial care factor on these images, please ensure
        // appropriate approvals have happened before replacing or updating
        <StyledSubscriptionPackage packageTheme={packageTheme}>
            {packageType === 'digital' && (
                <ComputedPackageImage section={section}></ComputedPackageImage>
            )}

            {packageType === 'digitalprint' && (
                <StyledPackageImage
                    src={everyDayNewspaperImage}
                    alt=""
                    role="presentation"
                />
            )}
            <StyledSubscriptionPackageHeading packageTheme={packageTheme}>
                {heading}
            </StyledSubscriptionPackageHeading>
            {originalPrice && (
                <StyledSubscriptionPackageOriginalPrice
                    packageTheme={packageTheme}
                >
                    {originalPrice}
                </StyledSubscriptionPackageOriginalPrice>
            )}
            <StyledSubscriptionPackagePrice packageTheme={packageTheme}>
                {price}
            </StyledSubscriptionPackagePrice>
            {priceQualifier && (
                <StyledSubscriptionPackagePriceQualifier
                    packageTheme={packageTheme}
                >
                    {priceQualifier}
                </StyledSubscriptionPackagePriceQualifier>
            )}
            {ribbonTitle && (
                <Ribbon packageTheme={packageTheme}>
                    <span>
                        <IconStar />
                        {ribbonTitle}
                    </span>
                </Ribbon>
            )}
            <StyledSubscriptionPackageActions>
                <StyledSubscriptionPackageButton
                    packageTheme={packageTheme}
                    buttonWidth="wide"
                    onClick={() => {
                        createLinkClickEvent(
                            breachCreativeName,
                            breachDeal,
                            onEvent,
                        )()
                        onSubscribeClick({
                            packagePath,
                            offerCode,
                            callToAction: click_origin_breach_screen,
                        })
                    }}
                >
                    Subscribe Now
                </StyledSubscriptionPackageButton>

                {packageType === 'digital' && showSwG && (
                    <StyledSubscribeWithGoogleButton ref={buttonRef} />
                )}
                <StyledSubscriptionCancellationMessage
                    packageTheme={packageTheme}
                >
                    {cancellationMessage}
                </StyledSubscriptionCancellationMessage>
            </StyledSubscriptionPackageActions>
        </StyledSubscriptionPackage>
    )
}

SubscriptionPackage.displayName = 'SubscriptionPackage'

export default SubscriptionPackage
